import { useEffect, useState } from "react";
import apiWithRefresh from "../utils/apiWithRefresh";
import useDarkMode from "./useDarkMode";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
  IconButton,
  Stack,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useToast } from "../utils/useToast";
import GlobalToast from "../components/GlobalToast";

const initialForm = {
  id: null,
  configAppId: "",
  countryCurrencyId: "",
  amount: "",
};

const AdminConfigAppPrices = () => {
  useDarkMode();
  const [prices, setPrices] = useState([]);
  const [configApps, setConfigApps] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(initialForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, showToast] = useToast();
  const [selectedCountryCurrencyId, setSelectedCountryCurrencyId] =
    useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pricesRes, configAppsRes, countriesRes] = await Promise.all([
        apiWithRefresh.get("configAppPrices"),
        apiWithRefresh.get("config"),
        apiWithRefresh.get("countrycurrency"),
      ]);
      setPrices(pricesRes);
      setConfigApps(configAppsRes);
      setCountries(countriesRes);
    } catch (err) {
      // error handling can be added here if needed
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Set default filter to first countrycurrency after countries are loaded
  useEffect(() => {
    if (countries.length > 0 && !selectedCountryCurrencyId) {
      setSelectedCountryCurrencyId(countries[0].id);
    }
  }, [countries, selectedCountryCurrencyId]);

  const openAddModal = () => {
    setForm(initialForm);
    setEditingId(null);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setForm({
      id: item.id,
      configAppId: item.configAppId,
      countryCurrencyId: item.countryCurrencyId,
      amount: item.amount,
    });
    setEditingId(item.id);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(initialForm);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleAddOrUpdate = async (e) => {
    e.preventDefault();
    if (!form.configAppId || !form.countryCurrencyId || !form.amount) {
      showToast("All fields are required.", "warning");
      return;
    }
    try {
      const payload = {
        configAppId: form.configAppId,
        countryCurrencyId: form.countryCurrencyId,
        amount: form.amount,
      };
      if (editingId) {
        await apiWithRefresh.put(`configAppPrices/${editingId}`, payload);
        showToast("ConfigAppPrice updated successfully!", "success");
      } else {
        await apiWithRefresh.post("configAppPrices", payload);
        showToast("ConfigAppPrice added successfully!", "success");
      }
      setModalOpen(false);
      setEditingId(null);
      setForm(initialForm);
      fetchAll();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const openDeleteConfirm = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await apiWithRefresh.delete(`configAppPrices/${deleteId}`);
      showToast("ConfigAppPrice deleted successfully!", "success");
      setConfirmOpen(false);
      setDeleteId(null);
      fetchAll();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const columns = [
    {
      field: "configApp",
      headerName: "Type",
      flex: 1,
      valueGetter: (params) => params.name || "",
    },
    { field: "amount", headerName: "Amount", flex: 1 },

    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton color="primary" onClick={() => openEditModal(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => openDeleteConfirm(params.row.id)}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      ),
      width: 120,
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <GlobalToast toast={toast} />
      <Typography variant="h5" gutterBottom>
        Admission Fees
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Select
          value={selectedCountryCurrencyId}
          onChange={(e) => setSelectedCountryCurrencyId(e.target.value)}
          displayEmpty
          sx={{ minWidth: 220, mr: 2 }}
        >
          {countries.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.country} ({c.code})
            </MenuItem>
          ))}
        </Select>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openAddModal}
        >
          Add Price
        </Button>
      </Box>
      <div className="responsiveTable" style={{ height: 500 }}>
        <DataGrid
          rows={
            selectedCountryCurrencyId
              ? prices.filter(
                  (p) => p.countryCurrencyId === selectedCountryCurrencyId,
                )
              : prices
          }
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          loading={loading}
          disableSelectionOnClick
          getRowId={(row) => row.id}
        />
      </div>
      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{editingId ? "Edit Price" : "Add Price"}</DialogTitle>
        <form onSubmit={handleAddOrUpdate}>
          <DialogContent>
            <Select
              name="configAppId"
              value={form.configAppId}
              onChange={handleChange}
              fullWidth
              required
              displayEmpty
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Select Config App</MenuItem>
              {configApps.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
            <Select
              name="countryCurrencyId"
              value={form.countryCurrencyId}
              onChange={handleChange}
              fullWidth
              required
              displayEmpty
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Select Country</MenuItem>
              {countries.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.country} ({c.code})
                </MenuItem>
              ))}
            </Select>
            <TextField
              name="amount"
              label="Amount"
              type="number"
              value={form.amount}
              onChange={handleChange}
              fullWidth
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingId ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Are you sure you want to delete this price?
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminConfigAppPrices;
